import React, { useState, useEffect } from 'react';
import { CiEdit } from 'react-icons/ci'
import axios from 'axios'

// const BASE_URL = "https://staging.pteshstudios.com";
const BASE_URL = "https://api.jeyshawnng.com";

const resolveSrc = (value, fallback = null) => {
  if (!value) return fallback;
  if (value instanceof File) return URL.createObjectURL(value);
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return fallback;
};

/* ─────────────────────────────────────────────
   LOCAL CACHE HELPERS (cache-first, DB-driven)
───────────────────────────────────────────── */
const CACHE_KEY = "jeyshawn_projects_data";

const getCachedData = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedData = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage full/unavailable — fail silently, page still works
  }
};

const STATUS_OPTIONS = ["Ongoing", "Completed"];

// ─── Edit Modal ───────────────────────────────────────────────────────────────
const EditModal = ({ isOpen, onClose, pageData, onRefresh }) => {
  const [heroImage, setHeroImage] = useState(null);
  const [closingText, setClosingText] = useState("");
  const [projects, setProjects] = useState([]);
  const [editStates, setEditStates] = useState({});
  const [editLoadings, setEditLoadings] = useState({});
  const [reorderLoading, setReorderLoading] = useState(false);
  const [newProject, setNewProject] = useState({ project: "", yearCompleted: "Ongoing", client: null });
  const [addLoading, setAddLoading] = useState(false);
  const [heroLoading, setHeroLoading] = useState(false);

  useEffect(() => {
    if (pageData && isOpen) {
      setHeroImage(null);
      setClosingText(pageData.closingText || "");

      const sorted = [...(pageData.projects || [])].sort((a, b) => a.order - b.order);
      setProjects(sorted);

      const states = {};
      sorted.forEach((p) => {
        states[p._id] = {
          project: p.project || "",
          yearCompleted: p.yearCompleted || "Ongoing",
          client: p.client || null,
        };
      });
      setEditStates(states);
    }
  }, [pageData, isOpen]);

  if (!isOpen) return null;

  const handleSaveHero = async () => {
    setHeroLoading(true);
    try {
      const form = new FormData();
      form.append("closingText", closingText);
      if (heroImage instanceof File) form.append("heroImage", heroImage);

      await axios.put(`${BASE_URL}/api/project-table`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onRefresh();
    } catch (err) {
      console.error("Failed to update hero:", err);
    } finally {
      setHeroLoading(false);
    }
  };

  const handleEditField = (id, field, value) => {
    setEditStates((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleEditFile = (id, file) => {
    setEditStates((prev) => ({ ...prev, [id]: { ...prev[id], client: file } }));
  };

  const handleSaveProject = async (id, order) => {
    setEditLoadings((prev) => ({ ...prev, [id]: true }));
    try {
      const p = editStates[id];
      const form = new FormData();
      form.append("project", p.project);
      form.append("yearCompleted", p.yearCompleted);
      form.append("order", order);
      if (p.client instanceof File) form.append("client", p.client);

      await axios.patch(`${BASE_URL}/api/project-table/project/${id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onRefresh();
    } catch (err) {
      console.error("Failed to update project:", err);
    } finally {
      setEditLoadings((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/project-table/project/${id}`);
      setProjects((prev) => prev.filter((p) => p._id !== id));
      onRefresh();
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  const handleMove = async (index, direction) => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= projects.length) return;
    const newList = [...projects];
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    setProjects(newList);
    setReorderLoading(true);
    try {
      await axios.patch(`${BASE_URL}/api/project-table/reorder`, {
        projects: newList.map((p, i) => ({ id: p._id, order: i })),
      });
      onRefresh();
    } catch (err) {
      console.error("Failed to reorder projects:", err);
    } finally {
      setReorderLoading(false);
    }
  };

  const handleAddProject = async () => {
    setAddLoading(true);
    try {
      const form = new FormData();
      form.append("project", newProject.project);
      form.append("yearCompleted", newProject.yearCompleted);
      form.append("order", projects.length);
      if (newProject.client instanceof File) form.append("client", newProject.client);

      await axios.post(`${BASE_URL}/api/project-table/project`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNewProject({ project: "", yearCompleted: "Ongoing", client: null });
      onRefresh();
    } catch (err) {
      console.error("Failed to add project:", err);
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div className="em-overlay" onClick={onClose}>
      <div className="em-modal" onClick={(e) => e.stopPropagation()}>

        <div className="em-header">
          <h2 className="em-title">Edit Projects Table</h2>
          <button className="em-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="em-body">

          {/* ── HERO & CLOSING ── */}
          <div className="em-section">
            <h3 className="em-section-title">Page Settings</h3>
            <div className="em-field">
              <label className="em-label">Hero Image</label>
              <input
                className="em-file-input"
                type="file"
                accept="image/*"
                onChange={(e) => setHeroImage(e.target.files[0])}
              />
              {heroImage instanceof File ? (
                <img src={URL.createObjectURL(heroImage)} alt="Hero preview" className="em-file-preview" />
              ) : pageData?.heroImage ? (
                <img src={resolveSrc(pageData.heroImage)} alt="Current hero" className="em-file-preview" />
              ) : null}
            </div>
            <div className="em-field">
              <label className="em-label">Closing Text</label>
              <textarea
                className="em-textarea"
                rows={3}
                value={closingText ?? ""}
                onChange={(e) => setClosingText(e.target.value)}
              />
            </div>
            <button className="em-btn-save" onClick={handleSaveHero} disabled={heroLoading}>
              {heroLoading ? "Saving..." : "Save Settings"}
            </button>
          </div>

          <div className="em-divider" />

          {/* ── EXISTING PROJECTS ── */}
          <div className="em-section">
            <h3 className="em-section-title">
              Projects {reorderLoading && <span className="em-hint">(Reordering...)</span>}
            </h3>

            {projects.map((project, index) => {
              const state = editStates[project._id] || {};
              const isLoading = editLoadings[project._id] || false;
              return (
                <div className="em-expertise-card" key={project._id}>
                  <div className="em-expertise-card-header">
                    <span className="em-expertise-num">Project {String(index + 1).padStart(2, "0")}</span>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      <button className="em-remove-btn" onClick={() => handleMove(index, "up")} disabled={index === 0 || reorderLoading} title="Move up">↑</button>
                      <button className="em-remove-btn" onClick={() => handleMove(index, "down")} disabled={index === projects.length - 1 || reorderLoading} title="Move down">↓</button>
                      <button className="em-remove-btn" onClick={() => handleDeleteProject(project._id)} aria-label="Delete">&times;</button>
                    </div>
                  </div>

                  <div className="em-field">
                    <label className="em-label">Client Logo</label>
                    <input className="em-file-input" type="file" accept="image/*"
                      onChange={(e) => handleEditFile(project._id, e.target.files[0])} />
                    {state.client instanceof File ? (
                      <img src={URL.createObjectURL(state.client)} alt="New logo preview" className="em-file-preview" style={{ width: "200px", height: "80px", objectFit: "contain" }} />
                    ) : state.client ? (
                      <img src={resolveSrc(state.client)} alt="Current logo" className="em-file-preview" style={{ width: "200px", height: "80px", objectFit: "contain" }} />
                    ) : null}
                  </div>

                  <div className="em-field">
                    <label className="em-label">Project Title</label>
                    <textarea className="em-textarea" rows={3} placeholder="Project title..."
                      value={state.project ?? ""}
                      onChange={(e) => handleEditField(project._id, "project", e.target.value)} />
                  </div>

                  <div className="em-field">
                    <label className="em-label">Status</label>
                    <select className="em-input" value={state.yearCompleted ?? "Ongoing"}
                      onChange={(e) => handleEditField(project._id, "yearCompleted", e.target.value)}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <button className="em-btn-save" onClick={() => handleSaveProject(project._id, index)} disabled={isLoading}>
                    {isLoading ? "Saving..." : "Save Project"}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="em-divider" />

          {/* ── ADD NEW PROJECT ── */}
          <div className="em-section">
            <h3 className="em-section-title">Add New Project</h3>

            <div className="em-field">
              <label className="em-label">Client Logo</label>
              <input className="em-file-input" type="file" accept="image/*"
                onChange={(e) => setNewProject((p) => ({ ...p, client: e.target.files[0] }))} />
              {newProject.client instanceof File && (
                <img src={URL.createObjectURL(newProject.client)} alt="New client preview" className="em-file-preview" style={{ width: "200px", height: "80px", objectFit: "contain" }} />
              )}
            </div>

            <div className="em-field">
              <label className="em-label">Project Title</label>
              <textarea className="em-textarea" rows={3} placeholder="Project title..."
                value={newProject.project ?? ""}
                onChange={(e) => setNewProject((p) => ({ ...p, project: e.target.value }))} />
            </div>

            <div className="em-field">
              <label className="em-label">Status</label>
              <select className="em-input" value={newProject.yearCompleted ?? "Ongoing"}
                onChange={(e) => setNewProject((p) => ({ ...p, yearCompleted: e.target.value }))}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <button className="em-btn-save" onClick={handleAddProject} disabled={addLoading || !newProject.project}>
              {addLoading ? "Adding..." : "+ Add Project"}
            </button>
          </div>

        </div>

        <div className="em-footer">
          <button className="em-btn-cancel" onClick={onClose}>Close</button>
        </div>

      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Data comes only from the DB. On first ever visit (no cache) it loads from
 * the API in the foreground. On return visits it renders instantly from
 * localStorage, then silently re-checks the DB in the background (and on a
 * 30s interval) and only updates the UI + cache if the DB data changed.
 */
const Projects = () => {
  const [pageData, setPageData] = useState(() => getCachedData());
  const [loading, setLoading] = useState(() => !getCachedData());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const fetchData = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      setFetchError(null);
    }
    try {
      const { data } = await axios.get(`${BASE_URL}/api/project-table`);
      const payload = data?.data ?? data;

      if (payload) {
        const cached = getCachedData();
        const changed = JSON.stringify(cached) !== JSON.stringify(payload);
        if (changed) {
          setPageData(payload);
          setCachedData(payload);
        }
      } else if (!isBackground && !getCachedData()) {
        setPageData(null);
      }
    } catch (err) {
      console.error("Failed to fetch project table data:", err);
      // Background syncs, or foreground loads when we already have cached
      // data, fail silently so the page keeps showing what it has.
      if (!isBackground && !getCachedData()) {
        setPageData(null);
        setFetchError(err.message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    } finally {
      if (!isBackground) setTimeout(() => setLoading(false), 400);
    }
  };

  useEffect(() => {
    const hasCache = !!getCachedData();
    fetchData(hasCache); // silent sync if we already have cached data to show

    const interval = setInterval(() => fetchData(true), 30000); // auto-check DB every 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DB data only — projects with no resolvable client logo just render without one.
  const displayProjects = Array.isArray(pageData?.projects)
    ? [...pageData.projects].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const closingText = pageData?.closingText || "";
  const heroImg      = resolveSrc(pageData?.heroImage);

  const isGrayRow = (rowIndex) => rowIndex % 2 === 0;

  if (loading) {
    return (
      <div style={{
        position: "fixed", inset: 0,
        background: "#0a0a0a",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        zIndex: 9999,
      }}>
        <div style={{
          width: "48px", height: "48px",
          border: "3px solid rgba(255,255,255,0.1)",
          borderTop: "3px solid #ffffff",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
          marginBottom: "20px",
        }} />
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Loading
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className='color_project'>
      <div className="img_carrierpj" style={{ position: "relative" }}>
        <button
          className="edit-btn"
          title="Edit page"
          onClick={() => setIsModalOpen(true)}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 10,
            background: "rgba(255,255,255,0.9)",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <CiEdit size={24} color="#333" />
        </button>
        {heroImg && <img src={heroImg} alt="img" />}
      </div>

      {displayProjects.length > 0 && (
        <div className="container">
          <div className="table-container">
            <table className="data-table">
              <tbody>
                {displayProjects.map((project, projectIndex) => {
                  const clientLogo = resolveSrc(project.client);
                  return (
                    <React.Fragment key={project._id || project.id}>
                      <tr className={isGrayRow(0) ? 'row-gray' : 'row-white'}>
                        <td className="col-label">Project:</td>
                        <td className="col-value">{project.project}</td>
                      </tr>

                      <tr className={isGrayRow(2) ? 'row-gray' : 'row-white'}>
                        <td className="col-label"></td>
                        <td className="col-value"></td>
                      </tr>

                      {clientLogo && (
                        <tr className={isGrayRow(3) ? 'row-gray' : 'row-white'}>
                          <td className="col-label">Client:</td>
                          <td className="col-value">
                            <img
                              src={clientLogo}
                              alt="Client"
                              style={{ width: "200px", height: "80px", objectFit: "contain" }}
                            />
                          </td>
                        </tr>
                      )}

                      <tr className={isGrayRow(1) ? 'row-gray' : 'row-white'}>
                        <td className="col-label">Status:</td>
                        <td className="col-value">{project.yearCompleted}</td>
                      </tr>

                      {projectIndex < displayProjects.length - 1 && (
                        <>
                          <tr className="empty-row row-gray">
                            <td className="col-label"></td>
                            <td className="col-value"></td>
                          </tr>
                          <tr className="empty-row row-white">
                            <td className="col-label"></td>
                            <td className="col-value"></td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {closingText && (
              <p style={{ fontWeight: 'bolder', marginTop: '2rem', fontSize: '20px' }}>
                {closingText}
              </p>
            )}
          </div>

          <style jsx>{`
            .container {
              max-width: 1000px;
              margin: 0 auto;
              padding: 40px;
              font-family: Arial, Helvetica, sans-serif;
            }
            .color_project{
              background: #e5fce5;
            }
            .year-title {
              font-size: 42px;
              font-weight: normal;
              color: #333;
              margin: 0 0 25px 0;
              padding: 0;
            }

            .table-container {
              width: 100%;
              background: #e5fce5 !important;
            }

            .data-table {
              width: 100%;
              border-collapse: collapse;
              border-spacing: 0;
            }

            .data-table td {
              padding: 14px 16px;
              font-size: 15px;
              font-weight: bold;
              line-height: 1.5;
              border-bottom: 1px solid #000;
              vertical-align: top;
            }

            .row-gray td {
              background-color: #f7bb2f;
            }

            .row-white td {
              background-color: #e5fce5;
            }

            .col-label {
              width: 22%;
              color: #444;
              font-weight: normal;
              border-right: 1px solid #000;
            }

            .col-value {
              width: 78%;
              color: #333;
            }

            .empty-row td {
              height: 32px;
              padding: 0 16px;
            }

            @media (max-width: 768px) {
              .container {
                padding: 20px;
              }
              .year-title {
                font-size: 32px;
              }
              .data-table td {
                padding: 12px 14px;
                font-size: 12px;
              }
              .col-label {
                width: 30%;
              }
              .col-value {
                width: 70%;
              }
            }

            @media (max-width: 480px) {
              .container {
                padding: 15px;
              }
              .year-title {
                font-size: 28px;
                margin-bottom: 15px;
              }
              .data-table {
                display: block;
              }
              .data-table tbody {
                display: block;
              }
              .data-table tr {
                display: flex;
                flex-direction: column;
                margin-bottom: 8px;
              }
              .col-label {
                width: 100%;
                border-right: none;
                font-weight: 600;
                padding: 10px 14px;
              }
              .col-value {
                width: 100%;
                padding: 12px 14px;
              }
              .empty-row {
                display: none;
              }
            }
          `}</style>
        </div>
      )}

      <EditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        pageData={pageData}
        onRefresh={() => fetchData(false)}
      />

      {showToast && (
        <div className="em-toast em-toast--error" style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          background: "#dc2626",
          color: "white",
          padding: "16px 20px",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
        }}>
          <span>✕</span>
          <div>
            <p style={{ fontWeight: 600, margin: 0 }}>Failed to load page data</p>
            <p style={{ fontSize: "13px", margin: "4px 0 0 0", opacity: 0.9 }}>{fetchError}</p>
          </div>
          <button onClick={() => setShowToast(false)} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: "16px" }}>✕</button>
        </div>
      )}
    </div>
  );
};

export default Projects;